<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div v-if="selectedReporting !== null && initiallist[selectedReporting]?.fromList" :class="primaryFieldClass">
      <span class="block text-sm font-medium leading-6 text-surface-700 mb-2"> {{ startDateLabel }} </span>
      <DatePicker v-model="startdateModel" show-icon :show-on-focus="false" show-button-bar class="w-full" />
      <Message
        v-if="errorMessageStartDate" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
        class="mb-2">
        {{ errorMessageStartDate }}
      </Message>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div v-if="selectedReporting !== null && initiallist[selectedReporting]?.toList" class="mb-5">
      <span class="block text-sm font-medium leading-6 text-surface-700 mb-2"> {{ endDateLabel }} </span>
      <DatePicker v-model="enddateModel" show-icon :show-on-focus="false" show-button-bar class="w-full" />
      <Message
        v-if="errorMessageEndDate" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
        class="mb-2">
        {{ errorMessageEndDate }}
      </Message>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div v-if="selectedReporting !== null && initiallist[selectedReporting]?.cityList" :class="primaryFieldClass">
      <span class="block text-sm font-medium leading-6 text-surface-700 mb-2"> Ville </span>
      <ModelSearch v-model="selectedCityModel" api-url="/api/cities" :no-pagination="true" class="mt-3" />
      <Message
        v-if="errorMessageCity" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
        class="mb-2">
        {{ errorMessageCity }}
      </Message>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div v-if="selectedReporting !== null && initiallist[selectedReporting]?.allCitiesList" class="mb-5">
      <span class="block text-sm font-medium leading-6 text-surface-700 mb-2"> Ville </span>
      <Select
        v-model="selectedCityModel" :options="allCities" show-clear option-label="name" option-value="value"
        placeholder="-- Sélectionner une ville --" class="w-full md:w-14rem" :loading="!isDataLoaded" />
      <Message
        v-if="errorMessageAllCity" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
        class="mb-2">
        {{ errorMessageAllCity }}
      </Message>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div v-if="selectedReporting !== null && initiallist[selectedReporting]?.marketList" class="mb-5">
      <span class="block text-sm font-medium leading-6 text-surface-700 mb-2"> Marché </span>
      <Select
        v-model="selectedMarketModel" :options="allMarkets" show-clear option-label="name" option-value="value"
        placeholder="-- Sélectionner un marché --" class="w-full md:w-14rem" :loading="!isDataLoaded" />
      <Message
        v-if="errorMessageMarket" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
        class="mb-2">
        {{ errorMessageMarket }}
      </Message>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-300 ease-in-out" enter-from-class="opacity-0 -translate-y-2"
    leave-active-class="transition-all duration-300 ease-in-out" leave-to-class="opacity-0 translate-y-2">
    <div
      v-if="(selectedReporting !== null && initiallist[selectedReporting]?.multipleMarketList) || selectedReporting === 3"
      class="mb-5">
      <div v-if="(selectedCityModel ?? 0) > 0 && cityRegroupValue === 0">
        <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Marchés</span>
        <MultiSelect
          v-model="selectedMarketsModel" :options="markets" option-label="name" option-value="value" filter
          display="chip" :placeholder="`Sélectionner des marchés pour la ville de ${cityName[selectedCityModel ?? 0]}`"
          class="w-full md:w-[22rem] text-sm" :max-selected-labels="500" show-clear panel-class="text-sm" :pt="{
            root: { class: 'flex-wrap items-start py-2 px-3' },
            label: { class: 'text-sm' },
            item: { class: 'py-2 px-3 text-sm' },
            header: { class: 'py-2 px-3 text-sm' },
            filterInput: { class: 'text-sm py-1' },
            token: { class: 'text-sm px-2 py-1 m-1 rounded bg-primary-100 text-primary-800' },
            trigger: { class: 'px-2' },
          }" />
        <Message
          v-if="errorMessageMarkets" severity="error" icon="pi pi-times-circle" variant="simple" size="small"
          class="mb-2">
          {{ errorMessageMarkets }}
        </Message>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import MultiSelect from 'primevue/multiselect';
import ModelSearch from '~/components/atoms/model/search';

defineProps<{
  selectedReporting: number | null;
  initiallist: Record<number, Record<string, boolean>>;
  primaryFieldClass: string;
  startDateLabel: string;
  endDateLabel: string;
  errorMessageStartDate: string;
  errorMessageEndDate: string;
  errorMessageCity: string;
  errorMessageAllCity: string;
  errorMessageMarket: string;
  errorMessageMarkets: string;
  allCities: Array<{ name: string; value: number }>;
  allMarkets: Array<{ name: string; value: number }>;
  markets: Array<{ name: string; value: number }>;
  cityName: Record<number, string>;
  cityRegroupValue: number;
  isDataLoaded: boolean;
}>();

const startdateModel = defineModel<string | Date | null>('startdate', { required: true });
const enddateModel = defineModel<string | Date | null>('enddate', { required: true });
const selectedCityModel = defineModel<number | null>('selectedCity', { required: true });
const selectedMarketModel = defineModel<string>('selectedMarket', { required: true });
const selectedMarketsModel = defineModel<number[]>('selectedMarkets', { required: true });
</script>
